import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  ArrowDownRight, ArrowUpRight, CalendarCheck, Flame, MessageSquare, TrendingUp, Users,
} from "lucide-react";
import { AppShell } from "@/shared/layouts/AppShell.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { LoadingState } from "@/shared/ui/spinner.jsx";
import { api } from "@/api/client.js";
import { formatNumber } from "@/shared/lib/format.js";
import { STAGES } from "@/features/leads/lead-constants.js";

/**
 * Visão executiva.
 *
 * Todos os números vêm calculados do servidor. Na tela antiga, o gráfico de
 * "Leads vs Agendamentos" era gerado com `Math.sin()` e `Math.random()`, a
 * origem dos leads era uma lista fixa no código e as variações "+18%" eram
 * texto digitado à mão — nada daquilo refletia a operação.
 */

const CORES_ORIGEM = ["#3b82f6", "#22d3ee", "#6366f1", "#0ea5e9", "#7dd3fc", "#a5b4fc"];

const ESTILO_TOOLTIP = {
  background: "var(--color-popover)",
  border: "1px solid var(--color-border)",
  borderRadius: 12,
  fontSize: 12,
};

export function DashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get("/dashboard"),
  });

  if (isLoading || !data) {
    return (
      <AppShell title="Dashboard Geral" subtitle="Carregando dados comerciais...">
        <LoadingState message="Calculando indicadores..." />
      </AppShell>
    );
  }

  const { counters, deltas, daily, byOrigin, byOwner, funnel } = data;

  const origemComCor = byOrigin.map((item, i) => ({
    ...item,
    color: CORES_ORIGEM[i % CORES_ORIGEM.length],
  }));

  return (
    <AppShell title="Dashboard Geral" subtitle="Visão executiva da operação comercial">
      <div className="space-y-6 p-6">
        {/* ----------------------------------------------------- KPIs */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Kpi label="Leads no período" value={counters.total} delta={deltas.leads} icon={Users} accent="primary" />
          <Kpi label="Atendimentos" value={counters.atendimentos} icon={MessageSquare} accent="chart-2" />
          <Kpi label="Taxa de conversão" value={`${counters.taxaConversao}%`} icon={TrendingUp} accent="success" />
          <Kpi
            label="Consultas agendadas"
            value={counters.agendados}
            delta={deltas.agendamentos}
            icon={CalendarCheck}
            accent="warning"
          />
        </div>

        {/* ------------------------------------- série diária + origem */}
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="font-display text-base">Leads vs Agendamentos</CardTitle>
                <p className="text-xs text-muted-foreground">Últimos 14 dias</p>
              </div>
              <Badge variant="secondary" className="gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                WhatsApp First
              </Badge>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer>
                  <AreaChart data={daily} margin={{ left: -20, right: 8, top: 8 }}>
                    <defs>
                      <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--color-primary)" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="var(--color-primary)" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gAgendamentos" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--color-chart-2)" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="var(--color-chart-2)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="day" stroke="var(--color-muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="var(--color-muted-foreground)" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={ESTILO_TOOLTIP} />
                    <Area type="monotone" dataKey="leads" name="Leads" stroke="var(--color-primary)" strokeWidth={2} fill="url(#gLeads)" />
                    <Area type="monotone" dataKey="agendamentos" name="Agendamentos" stroke="var(--color-chart-2)" strokeWidth={2} fill="url(#gAgendamentos)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-display text-base">Origem dos leads</CardTitle>
              <p className="text-xs text-muted-foreground">Distribuição por canal</p>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={origemComCor} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                      {origemComCor.map((item) => (
                        <Cell key={item.name} fill={item.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={ESTILO_TOOLTIP} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 space-y-1.5">
                {origemComCor.map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <span className="h-2 w-2 rounded-full" style={{ background: item.color }} />
                      {item.name}
                    </span>
                    <span className="font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* --------------------------------- temperatura + desempenho */}
        <div className="grid gap-4 lg:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-display text-base">Temperatura dos leads</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { rotulo: "Quentes", valor: counters.quentes, cor: "hot" },
                { rotulo: "Mornos", valor: counters.mornos, cor: "warm" },
                { rotulo: "Frios", valor: counters.frios, cor: "cold" },
              ].map((linha) => (
                <div
                  key={linha.rotulo}
                  className="flex items-center gap-3 rounded-lg border border-border/60 bg-card p-3"
                >
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg bg-${linha.cor}/15 text-${linha.cor}`}>
                    <Flame className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-muted-foreground">{linha.rotulo}</p>
                    <p className="font-display text-xl font-semibold">{linha.valor}</p>
                  </div>
                  <Badge variant="secondary">
                    {counters.total > 0 ? Math.round((linha.valor / counters.total) * 100) : 0}%
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-display text-base">Desempenho por atendente</CardTitle>
              <Button asChild variant="ghost" size="sm">
                <Link to="/crm">Ver CRM</Link>
              </Button>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer>
                  <BarChart data={byOwner} margin={{ left: -20, right: 8, top: 8 }}>
                    <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" stroke="var(--color-muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="var(--color-muted-foreground)" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={ESTILO_TOOLTIP} />
                    <Bar dataKey="convs" name="Leads" radius={[6, 6, 0, 0]} fill="var(--color-primary)" />
                    <Bar dataKey="conv" name="Agendados" radius={[6, 6, 0, 0]} fill="var(--color-chart-2)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ---------------------------------------------------- funil */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="font-display text-base">Funil comercial</CardTitle>
            <p className="text-xs text-muted-foreground">Distribuição atual no pipeline</p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {STAGES.map((etapa) => (
                <Link
                  key={etapa.id}
                  to={`/crm`}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition-opacity hover:opacity-80 ${etapa.tone}`}
                >
                  <span>{etapa.title}</span>
                  <Badge variant="secondary" className="h-5 bg-background/60">
                    {funnel[etapa.id] ?? 0}
                  </Badge>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

/**
 * Cartão de indicador. `delta` é opcional — quando não há base de
 * comparação confiável, o cartão simplesmente não mostra variação, em vez
 * de exibir um número inventado.
 */
function Kpi({ label, value, delta, icon: Icon, accent }) {
  const positivo = (delta ?? 0) >= 0;
  const Seta = positivo ? ArrowUpRight : ArrowDownRight;

  return (
    <Card className="relative overflow-hidden">
      <div
        className="absolute -right-6 -top-6 h-24 w-24 rounded-full opacity-10 blur-2xl"
        style={{ background: `var(--color-${accent})` }}
      />
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground">{label}</p>
            <p className="mt-2 font-display text-3xl font-semibold tracking-tight">
              {typeof value === "number" ? formatNumber(value) : value}
            </p>
          </div>
          <div className={`flex h-10 w-10 items-center justify-center rounded-xl bg-${accent}/10 text-${accent}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>

        {delta !== undefined && (
          <div className="mt-3 flex items-center gap-1.5 text-xs">
            <Seta className={`h-3.5 w-3.5 ${positivo ? "text-success" : "text-destructive"}`} />
            <span className={`font-medium ${positivo ? "text-success" : "text-destructive"}`}>
              {positivo ? "+" : ""}
              {delta}%
            </span>
            <span className="text-muted-foreground">vs. semana anterior</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
